<template>
  <el-form
    class="child-membership-form"
    label-position="top"
    label-width="100px"
  >
    <el-row :gutter="15">
      <el-col :span="24">
        <el-form-item
          label="회원권"
          data-test="form-item-category"
        >
          <el-select
            v-model="form.parent_membership"
            placeholder="회원권을 선택해주세요"
            :disabled="disabled"
            data-test="form-item-membership-parent-membership"
            :popper-append-to-body="false"
            @change="onChangeParentMembership"
          >
            <el-option-group
              v-for="(group, index) in parentMembershipForSelectBox"
              :key="index"
              :label="group.label"
            >
              <el-option
                v-for="item in group.options"
                :key="item.value"
                :label="item.label"
                :value="item.value"
                :disabled="item.is_removed"
              >
                <section :class="item.is_removed? 'option-wrapper__removed' : 'option-wrapper__used'">
                  <span class="option-label">
                    {{ item.label }}
                  </span>
                  <span class="option-removed">
                    {{ item.is_removed ? '삭제됨' : '' }}
                  </span>
                </section>
              </el-option>
            </el-option-group>
          </el-select>
        </el-form-item>
      </el-col>

      <el-col :span="24">
        <el-row :gutter="15">
          <el-col :span="12">
            <el-form-item label="회원권 시작일">
              <el-date-picker
                class="responsive-visible desktop"
                v-model="form.start"
                type="date"
                value-format="yyyy-MM-dd"
                :disabled="disabled"
                placeholder="시작일"
                data-test="date-picker-membership-start"
                @change="onChangeMembershipStartDate"
              />

              <input-date-mobile
                class="responsive-visible mobile"
                v-model="form.start"
                :disabled="disabled"
                @change="onChangeMembershipStartDate"
                @blur="onChangeMembershipStartDate"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="회원권 종료일">
              <el-date-picker
                class="responsive-visible desktop"
                v-model="form.end"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="종료일"
                :disabled="disabled"
                :picker-options="endDatePickerOption"
                data-test="date-picker-membership-end"
                @change="onChangeForm"
              />

              <input-date-mobile
                class="responsive-visible mobile"
                v-model="form.end"
                :minimum-date="form.start"
                minimum-date-warning-message="시작일 이전 날짜는 선택할 수 없습니다."
                :disabled="disabled"
                @blur="onChangeForm"
                @change="onChangeForm"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-col>
    </el-row>

    <el-row :gutter="15">
      <el-col :span="24">
        <el-form-item
          label="수업"
          data-test="form-parent-course"
        >
          <parent-course-select-box
            v-model="form.parent_courses"
            placeholder="수업을 모두 선택해주세요."
            :option-groups="parentCourseCategories"
            :fixed-value="publicParentCourseIds"
            :disabled="disabled"
            :show-is-removed="true"
            :is-error="hasParentCourseError"
            :error-message="'회원이 수강할 수 있는 수업을 한 개 이상 선택해 주세요.'"
            @change="onChangeForm"
            @init="resetParentCourseReletedValue"
          />
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="15">
      <el-col :span="12">
        <el-form-item
          :label="'최대 수강 ' + membershipSystem.long"
          data-test="form-balance"
        >
          <el-input-number-or-none-dialog
            v-model="form.balance"
            :unit="membershipSystem.short"
            :none-value="-1"
            :disabled="disabled"
            data-test="input-membership-balance"
            @change="onChangeForm"
            @input="onChangeForm"
          />
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <el-form-item>
          <div
            slot="label"
            class="consumed-membership__label"
          >
            {{ '수강 ' + membershipSystem.long }}
            <popover>
              본 회원권으로 이미 회원이 이용한 {{ membershipSystem.long }}를<br>
              의미합니다. 최신 회원권일 경우 '0'을<br>
              입력하시면 됩니다.
            </popover>
          </div>
          <div class="el-input-group__unit">
            <el-input-comma-number
              v-model="form.consumed"
              :min="0"
              :disabled="disabled"
              controls-position="right"
              data-test="input-membership-consumed"
              @focus="selectOnFocus"
              @change="onChangeForm"
              @input="onChangeForm"
            />
            <div class="input-unit">
              {{ membershipSystem.short }}
            </div>
          </div>
        </el-form-item>
      </el-col>
    </el-row>

    <div class="form-divide-line" />

    <el-row :gutter="15">
      <el-col :span="12">
        <el-form-item label="정상 금액">
          <div class="el-input-group__unit">
            <el-input
              v-model="membershipPrice"
              placeholder="자동 입력"
              disabled
            />
            <div class="input-unit">
              원
            </div>
          </div>
        </el-form-item>
      </el-col>

      <el-col :span="12">
        <el-form-item label="결제 금액">
          <div class="el-input-group__unit">
            <el-input
              placeholder="자동 입력"
              :value="paidAmount"
              disabled
            />
            <div class="input-unit">
              원
            </div>
          </div>
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="15">
      <el-col :span="12">
        <el-form-item label="카드">
          <div class="el-input-group__unit">
            <el-input
              v-model="payment.card"
              :controls="false"
              :min="0"
              :disabled="disabled"
              data-test="input-membership-payment-card"
              @focus="selectOnFocus"
              @input="paymentChangeSubscriber('card')"
              @blur="onChangePayment"
            />
            <div class="input-unit">
              원
            </div>
          </div>
        </el-form-item>
      </el-col>

      <el-col :span="12">
        <el-form-item label="현금">
          <div class="el-input-group__unit">
            <el-input
              v-model="payment.cash"
              :controls="false"
              :min="0"
              :disabled="disabled"
              data-test="input-membership-payment-cash"
              @focus="selectOnFocus"
              @input="paymentChangeSubscriber('cash')"
              @blur="onChangePayment"
            />
            <div class="input-unit">
              원
            </div>
          </div>
        </el-form-item>
      </el-col>
    </el-row>

    <el-row :gutter="15">
      <el-col :span="12">
        <el-form-item label="미수금">
          <div class="el-input-group__unit">
            <el-input
              v-model="payment.remainder"
              :controls="false"
              :min="0"
              :disabled="disabled"
              data-test="input-membership-payment-remainder"
              @focus="selectOnFocus"
              @input="paymentChangeSubscriber('remainder')"
              @blur="onChangePayment"
            />
            <div class="input-unit">
              원
            </div>
          </div>
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <el-form-item label="결제일">
          <el-date-picker
            class="responsive-visible desktop"
            v-model="form.payment.paid_at"
            type="date"
            value-format="yyyy-MM-dd"
            :disabled="disabled"
            placeholder="선택"
            data-test="date-membership-payment-paid-at"
            @change="onChangeForm"
          />
          <input-date-mobile
            class="responsive-visible mobile"
            v-model="form.payment.paid_at"
            :disabled="disabled"
            @blur="onChangeForm"
            @change="onChangeForm"
          />
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
import { makeCategoriesForSelectBox } from '@/utils/array-helper';
import { numberWithCommas, numberWithDeletedCommas } from '@/utils/etc';
import { computedGetters } from '@/utils/vuex-helper';
import { makeParentMembershipsForSelectBox } from '@/utils/array-helper.js';
import ParentCourseSelectBox from './ParentCourseSelectBox';
import dateFns from 'date-fns';
import ElInputNumberOrNoneDialog from '@/components/element-ui/ElInputNumOrNoneDialog';
import ElInputCommaNumber from '@/components/element-ui/ElInputCommaNumber';
import InputDateMobile from '@/components/InputDateMobile';
import Popover from '@/components/popover';
import { fiflboxApi } from '@/utils/api';

export default {
  name: 'ChildMembershipForm',
  components: {
    InputDateMobile,
    ElInputNumberOrNoneDialog,
    ElInputCommaNumber,
    ParentCourseSelectBox,
    Popover
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    originalData: {
      default: null
    },
    disabled: {
      default: false
    }
  },
  data() {
    return {
      form: {},
      paidAmount: null, // 결제 금액
      selectedParentMembership: null,
      categories: [],
      parentCourseCategories: [],
      payment: {
        card: 0,
        cash: 0,
        remainder: 0
      },
      initSelectedParentMembershipId: null,
      hasParentCourseError: false
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(originObject) {
        if (originObject === undefined) {
          return;
        }

        Object.keys(originObject).forEach(key => {
          this.$set(this.form, key, originObject[key]);
        });

        this.$emit('input', originObject);
      }
    },
    originalData: {
      immediate: true,
      deep: true,
      handler(originObject) {
        if (originObject !== null) {
          this.setPaidAmount(originObject.payment);
          this.initSelectedParentMembershipId = originObject.parent_membership;
        } else {
          this.paidAmount = null;
        }
      }
    }
  },
  async created() {
    await this.$store.dispatch('syncParentMembershipCategories', {});
    await this.$store.dispatch('syncParentMemberships');
    await this.syncParentCourses();
    await this.initalPaymentValueSet();
  },
  computed: {
    ...computedGetters([
      'membershipSystem',
      'publicParentCourseIds',
      'parentMembershipCategories',
      'parentMemberships'
    ]),
    endDatePickerOption() {
      let startDate = this.form.start;

      return {
        disabledDate(time) {
          if (startDate) {
            return dateFns.isBefore(time.getTime(), startDate);
          }

          return false;
        }
      };
    },
    membershipPrice() {
      if (!this.form.parent_membership) {
        return null;
      }

      const parentMembership = this.parentMemberships.find(
        membership => membership.id === this.form.parent_membership
      );
      if (!parentMembership) {
        return null;
      }
      return this.numberWithCommas(parentMembership.price);
    },
    parentMembershipForSelectBox() {
      return makeParentMembershipsForSelectBox(
        this.parentMembershipCategories,
        this.parentMemberships,
        this.initSelectedParentMembershipId
      );
    }
  },
  methods: {
    numberWithCommas,
    numberWithDeletedCommas,
    async syncParentCourses() {
      let { data } = await fiflboxApi.v2.getCourseCategories({
        is_removed: false
      });
      this.parentCourseCategories = makeCategoriesForSelectBox(
        data,
        'parent_courses'
      );
    },

    onChangeForm() {
      //below conditional for block dummy end-date value set in input.
      if (!this.form.start) {
        this.form.end = null;
      }
      this.$emit('input', this.form);
    },

    onChangeParentMembership(parentMembershipId) {
      this.selectedParentMembership = this.parentMemberships.find(
        membership => membership.id === parentMembershipId
      );

      if (this.selectedParentMembership !== null) {
        this.form.balance = this.selectedParentMembership.balance;
        //Below conditional for when membership change, end-date set automatically in input.
        if (this.form.start) {
          this.onChangeMembershipStartDate(this.form.start);
        }
        //if, there are no start-date, end-date set null.
        else {
          this.form.end = null;
        }
      }

      this.onChangeForm();
    },

    onChangeMembershipStartDate(startDate) {
      if (this.selectedParentMembership !== null) {
        this.form.end = dateFns
          .addDays(startDate, this.selectedParentMembership.duration - 1)
          .toDateFormat();
      }

      this.onChangeForm();
    },

    setPaidAmount({ cash, card }) {
      const paidAmount =
        this.numberWithDeletedCommas(cash) + this.numberWithDeletedCommas(card);

      if (Number.isNaN(paidAmount)) {
        return (this.paidAmount = '-');
      }

      return (this.paidAmount = numberWithCommas(paidAmount));
    },

    onChangePayment() {
      this.setPaidAmount(this.payment);
      for (let target in this.payment) {
        this.form.payment[target] = this.numberWithDeletedCommas(
          this.payment[target]
        );
      }
      this.onChangeForm();
    },

    selectOnFocus(event) {
      event.target.select();
    },
    initalPaymentValueSet() {
      if (this.form.payment) {
        const { card, cash, remainder } = this.form.payment;
        this.payment.card = this.numberWithCommas(card);
        this.payment.cash = this.numberWithCommas(cash);
        this.payment.remainder = this.numberWithCommas(remainder);
      }
    },
    paymentChangeSubscriber(target) {
      let deleteCommasValue = '';
      switch (target) {
        case 'card':
          deleteCommasValue = this.numberWithDeletedCommas(this.payment.card);
          break;
        case 'cash':
          deleteCommasValue = this.numberWithDeletedCommas(this.payment.cash);
          break;
        case 'remainder':
          deleteCommasValue = this.numberWithDeletedCommas(
            this.payment.remainder
          );
          break;
      }
      this.payment[target] = isNaN(deleteCommasValue)
        ? 0
        : this.numberWithCommas(deleteCommasValue);
    },
    validate() {
      let invalidFields = [];

      if (!Number.isInteger(this.value.parent_membership)) {
        invalidFields.push('수강할 회원권');
      }

      if (this.value.start === null) {
        invalidFields.push('시작일');
      }

      if (this.value.end === null) {
        invalidFields.push('종료일');
      }

      if (
        this.publicParentCourseIds.length + this.value.parent_courses.length <
        1
      ) {
        this.hasParentCourseError = true;
        invalidFields.push('수업');
      }

      if (!Number.isInteger(this.value.balance)) {
        invalidFields.push('최대 수강 횟수');
      }

      if (!Number.isInteger(this.value.consumed)) {
        invalidFields.push('수강 횟수');
      }

      if (!Number.isInteger(this.value.payment.card)) {
        invalidFields.push('카드 금액');
      }

      if (!Number.isInteger(this.value.payment.cash)) {
        invalidFields.push('현금');
      }

      if (!Number.isInteger(this.value.payment.remainder)) {
        invalidFields.push('미수금');
      }

      return {
        isValid: invalidFields.length === 0,
        message: invalidFields.join(', ')
      };
    },
    resetParentCourseReletedValue() {
      this.hasParentCourseError = false;
    }
  }
};
</script>

<style lang="scss">
.child-membership-option__wrapper,
.removed- {
  width: 100%;
  display: flex;
}

.consumed-membership__label {
  display: flex;
}
</style>
